/**
 * lists.js — the BUILT-IN Today lists and the refill statuses.
 *
 * Staff can add their own lists in the app (stored in LISTS_COLLECTION — see
 * logic/lists.js) and rename / recolour / reorder any list. Items store the id of the
 * list they were noted in as `category` (the Firestore field name predates "lists").
 *
 * List fields: `seeds` prime the autosuggest before any history exists (real history
 * always ranks above them). `sourceable` lists (physically bought) get the not-found /
 * out-of-stock flow; `noSuggest` marks the free-form Issues list (no suggestions);
 * `noQty` hides the optional quantity on task lists (cleaning/issues aren't amounts).
 * `claimsFrom` lets a list take over names noted in another list when its name rule
 * (logic/categorize.js CLAIM_RULES) matches — Chef Groceries was split out of Groceries,
 * so "Besan" / "Toor dal" noted under Groceries before the split show up under Chef.
 */
import { normalizeName } from '../logic/names';
import { closingNamesByList } from './closingSeed';

// Prep = made ready ahead (chop / cut / marinate / soak / portion / batter / refill);
// Cooking = on the stove (curries, gravies, house sauces, chutneys, rice). The split
// is what `logic/categorize.js` suggests from an item's name.
const BASE_LISTS = [
  {
    id: 'chef',
    label: 'Chef Groceries',
    short: 'Chef',
    icon: '🧑‍🍳',
    color: '#84cc16',
    sourceable: true,
    claimsFrom: ['groceries'],
    seeds: [
      'Toor dal', 'Chana dal', 'Urad dal', 'Moong dal', 'Peanuts', 'Large cashews',
      'Besan', 'Maida', 'Rice flour', 'Corn flour', 'Sooji', 'Basmati rice',
      'Oil', 'Ghee', 'Jeera', 'Mustard seeds', 'Turmeric', 'Red chili powder',
      'Masala powder', 'Garam masala', 'Biryani masala', 'Salt', 'Tamarind', 'Jaggery',
      'Curry leaves', 'Green chilies', 'Ginger', 'Garlic', 'Ginger-garlic paste',
      'Cilantro', 'Mint',
    ],
  },
  {
    id: 'groceries',
    label: 'Groceries & Ingredients',
    short: 'Groceries',
    icon: '🛒',
    color: '#f59e0b',
    sourceable: true,
    seeds: [
      'Onions', 'Tomatoes', 'Chicken', 'Goat / Mutton', 'Paneer', 'Milk',
      'Yogurt', 'Lettuce', 'Cheese', 'Eggs', 'Sugar', 'Boost', 'Sprite',
      'Frozen samosa', 'Frozen parota', 'Gulab jamun',
    ],
  },
  {
    id: 'supplies',
    label: 'To-Go Supplies',
    short: 'To-Go',
    icon: '🥡',
    color: '#3b82f6',
    sourceable: true,
    seeds: [
      '8oz cups & lids', '4oz cups & lids', 'Small togo boxes', 'Large togo boxes',
      '28oz bowls', '8oz bowls', 'Round plates', 'Butter papers', 'Vinyl gloves',
      'Foil trays', 'Aluminum foil', 'To-go bags', 'Napkins', 'Paper towels',
      'Container lids', 'Order labels',
    ],
  },
  {
    id: 'prep',
    label: 'Prep List',
    short: 'Prep',
    icon: '🔪',
    color: '#ec4899',
    seeds: [
      'Chopped onions', 'Chopped cilantro', 'Chopped ginger', 'Chopped garlic', 'Marinate chicken',
      'Tikka veggies', 'Soak rice', 'Boil eggs', 'Raita', 'Mango lassi', 'Chaat', 'Onion refill',
    ],
  },
  {
    id: 'cooking',
    label: 'Cooking List',
    short: 'Cooking',
    icon: '🍳',
    color: '#10b981',
    seeds: [
      'Dum biryani', 'Kurma', 'Fried rice', 'Chicken 65', 'Gobi 65', 'Chicken curry',
      'Fry onions (birista)', 'DB sauce', '65 sauce', 'Mint chutney', 'Tamarind chutney',
    ],
  },
  {
    id: 'cleaning',
    label: 'Cleaning & Closing',
    short: 'Cleaning',
    icon: '🧽',
    color: '#8b5cf6',
    noQty: true,
    seeds: [
      'Clean refrigerators', 'Clean fryer', 'Clean extra fryer', 'Store room cleanup',
      'Clean sambar bowl', 'Clean billing station', 'Clean equipment', 'Take out trash',
      'Mop floors', 'Sanitize counters', 'Clean prep tables', 'Wipe down stoves',
      'Restock cleaning supplies',
    ],
  },
  {
    id: 'issues',
    label: 'Issues & Notes',
    short: 'Issues',
    icon: '⚠️',
    color: '#f97316',
    noSuggest: true,
    noQty: true,
    seeds: [
      'Fridge not cooling', 'Freezer frosting', 'Equipment not working', 'Out of gas',
      'Leak / spill', 'Pest control needed', 'Item spoiled — discard', 'Left out too long',
    ],
  },
];

/** `base` plus each name in `extra` not already in it (matched by normalizeName). */
function withExtraSeeds(base, extra = []) {
  const seen = new Set(base.map(normalizeName));
  const out = [...base];
  extra.forEach(name => {
    const key = normalizeName(name);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(name);
  });
  return out;
}

// Every closing-sheet item is also a Today suggestion in the list it feeds.
const CLOSING_NAMES = closingNamesByList();
export const BUILTIN_LISTS = BASE_LISTS.map(l => ({ ...l, seeds: withExtraSeeds(l.seeds, CLOSING_NAMES[l.id]) }));

// Lists retired into tabs (Grocery Order → Shopping, Cutting → Cutting). Their old items
// are ignored everywhere.
export const RETIRED_LIST_IDS = ['grocery_order', 'cutting'];

// ── Lists made in the app ────────────────────────────────────────────────────
export const LIST_NAME_MAX = 40;
export const LIST_ICON_CHOICES = [
  '🧑‍🍳', '🛒', '🥡', '🔪', '🍳', '🧽', '⚠️', '🥬', '🥩', '🐟', '🧀', '🥛',
  '🍞', '🌶️', '🧂', '🫘', '🌾', '🧃', '🍦', '🍰', '🧊', '📦', '🧻', '🔧', '🧾', '📋', '⭐', '🎉',
];
export const LIST_COLOR_CHOICES = [
  '#f59e0b', '#84cc16', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#94a3b8',
];

// ── Refill status (sourceable buy-lists) ─────────────────────────────────────
// A bought item flows: needed → refilled | not_found | oos. Not-found / OOS
// surface on the Review tab for a manager to source & clear. Task lists only use
// needed ⇄ refilled ("done").
export const STATUS = {
  NEEDED: 'needed',
  REFILLED: 'refilled',
  NOT_FOUND: 'not_found',
  OOS: 'oos',
};
export const STATUS_META = {
  [STATUS.NEEDED]:    { label: 'Needed',       icon: '○',  color: '#94a3b8' },
  [STATUS.REFILLED]:  { label: 'Refilled',     icon: '✓',  color: '#10b981' },
  [STATUS.NOT_FOUND]: { label: 'Not found',    icon: '🔍', color: '#f59e0b' },
  [STATUS.OOS]:       { label: 'Out of stock', icon: '⛔', color: '#ef4444' },
};
export const FLAG_STATUSES = [STATUS.NOT_FOUND, STATUS.OOS];
// Order shown in the status menu (desktop) and the touch action sheet.
export const STATUS_ORDER = [STATUS.REFILLED, STATUS.NOT_FOUND, STATUS.OOS, STATUS.NEEDED];
